import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  BriefcaseIcon,
  CarIcon,
  HeartIcon,
  PlaneTakeoff,
  Dot,
  ChevronDown,
  ChevronUp,
  UserIcon,
  BuildingIcon,
  Users,
} from "lucide-react";
import Flag from "../../../../assets/images/Flag.png";
import Box from "./svg/Box";
import EmployeeDetails from "./EmployeeDetails";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import PageLoader from "../../../../assets/loaders/PageLoading";
import cancelIcon from "../../../../assets/images/cancelArrow.svg";
import role_employeeIcon1 from "../../../../assets/images/icons/icons8-male-user-50.png";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import PreviewModal from "../employeesModals/PreviewModal";
import DocumentViewerNew from "./DocumentViewerNew";

const EmployeeModal = ({
  isOpen,
  onClose,
  fetchedData,
  empPublicId,
  employeeGeneralInfo,
  handleGetTableData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [employmentInfo, setEmploymentInfo] = useState(null);
  const [salaryDetails, setSalaryDetails] = useState(null);
  const [iqamaDetails, setIqamaDetails] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [healthInsuranceDetails, setHealthInsuranceDetails] = useState(null);
  const [healthCardDetails, setHealthCardDetails] = useState(null);
  const [passportDetails, setPassportDetails] = useState(null);
  const [visaDetails, setVisaDetails] = useState(null);
  const [contractDetails, setContractDetails] = useState(null);
  const [drivingLicenseDetails, setDrivingLicenseDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [dependentsInfo, setDependentsInfo] = useState([]);
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  const [medicalDocuments, setMedicalDocuments] = useState([]);
  const [employmentDocuments, setEmploymentDocuments] = useState([]);
  const [personalInfoDisplay, setPersonalInfoDisplay] = useState([]);
  const [employeeRegistrationData, setEmployeeRegistrationData] = useState([]);
  const [isCitizen, setIsCitizen] = useState(false);
  const [immigrationInfo, setImmigrationInfo] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nationalIdDetails, setNationalIdDetails] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    employment: true,
    dependents: true, // Add this line
    medical: true,
    others: true,
  });

  const { fetchEmployeeRegistrationDataById } = MainServices();

  useEffect(() => {
    getEmployeeRegistrationData(empPublicId);
  }, [empPublicId]);

  console.log("setting vehicle docs: ", drivingLicenseDetails);

  useEffect(() => {
    // Set default vehicle document even if drivingLicenseDetails is null
    const defaultVehicleDoc = {
      type: t("Driving License"),
      number: drivingLicenseDetails?.drivingLicenseNo || "N/A",
      issueDate: drivingLicenseDetails?.issueDate || "N/A",
      expiryDate: drivingLicenseDetails?.drivingLicenseValidity
        ? formatDateToDotFormat(drivingLicenseDetails.drivingLicenseValidity)
        : "N/A",
      remainingLife: drivingLicenseDetails?.drivingLicenseValidity
        ? calculateRemainingLife(drivingLicenseDetails.drivingLicenseValidity)
        : "N/A",
      status: drivingLicenseDetails?.status || "No Data",
      label: "Driving License.png",
      documents: drivingLicenseDetails?.frontDocument || null,
    };

    setVehicleDocuments([defaultVehicleDoc]);
  }, [drivingLicenseDetails]);

  useEffect(() => {
    // Set default medical documents even if details are null
    const medicalDocs = [
      {
        type: t("Health Insurance"),
        number: healthInsuranceDetails?.insuranceNo || "N/A",
        issueDate: healthInsuranceDetails?.issueDate || "N/A",
        expiryDate: healthInsuranceDetails?.insuranceExpiry
          ? formatDateToDotFormat(healthInsuranceDetails.insuranceExpiry)
          : "N/A",
        remainingLife: healthInsuranceDetails?.insuranceExpiry
          ? calculateRemainingLife(healthInsuranceDetails.insuranceExpiry)
          : "N/A",
        status: healthInsuranceDetails?.status || "No Data",
        label: "Health Insurance.pdf",
        documents: healthInsuranceDetails?.frontDocument || null,
      },
      {
        type: t("Health Card"),
        number: healthCardDetails?.healthCardNo || "N/A",
        issueDate: "N/A",
        expiryDate: healthCardDetails?.healthCardExpiry
          ? formatDateToDotFormat(healthCardDetails.healthCardExpiry)
          : "N/A",
        remainingLife: healthCardDetails?.healthCardExpiry
          ? calculateRemainingLife(healthCardDetails.healthCardExpiry)
          : "N/A",
        status: healthCardDetails?.status || "No Data",
        label: "Health Card.pdf",
        documents: healthCardDetails?.backDocument || null,
      },
    ];

    setMedicalDocuments(medicalDocs);
  }, [healthInsuranceDetails, healthCardDetails]);

  useEffect(() => {
    // Set default employment documents even if details are null
    const employmentDocs = [
      {
        type: t("Contract"),
        number: contractDetails?.contractNo || "N/A",
        issueDate: "N/A",
        expiryDate: contractDetails?.contractEnd
          ? formatDateToDotFormat(contractDetails.contractEnd)
          : "N/A",
        remainingLife: contractDetails?.contractEnd
          ? calculateRemainingLife(contractDetails.contractEnd)
          : "N/A",
        status: contractDetails?.status || "No Data",
        label: "Employment.Pdf",
        documents: contractDetails?.frontDocument || null,
      },
    ];

    if (personalInfo?.citizen === "false") {
      employmentDocs.push({
        type: t("Iqama"),
        number: iqamaDetails?.iqamaId || "N/A",
        issueDate: iqamaDetails?.issueDate
          ? formatDateToDotFormat(iqamaDetails.issueDate)
          : "N/A",
        expiryDate: iqamaDetails?.iqamaIdExpiry
          ? formatDateToDotFormat(iqamaDetails.iqamaIdExpiry)
          : "N/A",
        remainingLife: iqamaDetails?.iqamaIdExpiry
          ? calculateRemainingLife(iqamaDetails.iqamaIdExpiry)
          : "N/A",
        status: iqamaDetails?.status || "No Data",
        label: "Iqama Document",
        documents: iqamaDetails?.frontDocument || null,
      });
    }

    setEmploymentDocuments(employmentDocs);
  }, [contractDetails, iqamaDetails, personalInfo?.citizen]);

  useEffect(() => {
    // Set default immigration documents even if details are null
    const immigrationDocs = [
      {
        type: t("Passport Details"),
        number: passportDetails?.passportNo || "N/A",
        issueDate: passportDetails?.issueDate
          ? formatDateToDotFormat(passportDetails.issueDate)
          : "N/A",
        expiryDate: passportDetails?.expiryDate
          ? formatDateToDotFormat(passportDetails.expiryDate)
          : "N/A",
        remainingLife: passportDetails?.expiryDate
          ? calculateRemainingLife(passportDetails.expiryDate)
          : "N/A",
        status: passportDetails?.status || "No Data",
        label: t("Passport"),
        documents: passportDetails?.frontDocument || null,
      },
    ];

    if (personalInfo?.citizen === "false") {
      immigrationDocs.push({
        type: t("Visa Details"),
        number: visaDetails?.visaNo || "N/A",
        issueDate: visaDetails?.issueDate
          ? formatDateToDotFormat(visaDetails.issueDate)
          : "N/A",
        expiryDate: visaDetails?.expiryDate
          ? formatDateToDotFormat(visaDetails.expiryDate)
          : "N/A",
        remainingLife: visaDetails?.expiryDate
          ? calculateRemainingLife(visaDetails.expiryDate)
          : "N/A",
        status: visaDetails?.status || "No Data",
        label: t("Visa"),
        documents: visaDetails?.frontDocument || null,
      });
    }

    setImmigrationInfo(immigrationDocs);
  }, [passportDetails, visaDetails, personalInfo?.citizen]);

  // Data-driven configuration for information sections
  const getInfoSections = () => [
    {
      key: "personal",
      title: t("Personal Information"),
      icon: <UserIcon className="w-6 h-6 md:w-8 md:h-8 text-[#1A6D73]" />,
      type: "info",
      data: personalInfo
        ? [
            { label: t("First Name"), value: personalInfo.firstName },
            {
              label: t("First Name (Arabic)"),
              value: personalInfo.firstNameArb,
            },
            { label: t("Second Name"), value: personalInfo.secondName },
            {
              label: t("Second Name (Arabic)"),
              value: personalInfo.secondNameArb,
            },
            { label: t("Third Name"), value: personalInfo.thirdName },
            {
              label: t("Third Name (Arabic)"),
              value: personalInfo.thirdNameArb,
            },
            { label: t("Last Name"), value: personalInfo.lastName },
            { label: t("Last Name (Arabic)"), value: personalInfo.lastNameArb },
            { label: t("Email"), value: personalInfo.email },
            {
              label: t("Contact Number"),
              value: personalInfo.phone || personalInfo.mobileNo,
            },
            { label: t("Gender"), value: personalInfo.gender },
            {
              label: t("Date of Birth (Hijri)"),
              value: personalInfo.dobHijri
                ? formatDateToDotFormat(personalInfo.dobHijri)
                : "N/A",
            },
            {
              label: t("Date of Birth (Gregorian)"),
              value: personalInfo.dobGregorian
                ? formatDateToDotFormat(personalInfo.dobGregorian)
                : "N/A",
            },
            {
              label: t("Age"),
              value: personalInfo.dobGregorian
                ? `${calculateAge(personalInfo.dobGregorian)} years`
                : "N/A",
            },
            { label: t("Religion"), value: personalInfo.name },
            { label: t("Marital Status"), value: personalInfo.maritalStatus },
            {
              label: t("Citizenship Status"),
              value:
                personalInfo.citizen === "true"
                  ? t("Citizen")
                  : t("Non-Citizen"),
            },
            {
              label: t("Nationality"),
              value:
                personalInfo.citizen === "true"
                  ? t("Saudi")
                  : personalInfo.nationalityInfo?.name,
            },
            {
              label: t("National ID"),
              value:
                nationalIdDetails?.nationalId ||
                personalInfo.nationalId ||
                "N/A",
            },
            {
              label: t("User Invitation"),
              value:
                personalInfo.userInvitation === "true" ? t("Yes") : t("No"),
            },
          ].filter(
            (item) =>
              item.value && item.value !== "N/A" && item.value.trim() !== ""
          )
        : [],
    },
    {
      key: "employment",
      title: t("Employment Information"),
      icon: <BuildingIcon className="w-6 h-6 md:w-8 md:h-8 text-[#1A6D73]" />,
      type: "info",
      data: employmentInfo
        ? [
            { label: t("Employee ID"), value: employmentInfo.employeeId },
            {
              label: t("Branch"),
              value: employmentInfo.branchInfo?.name,
            },
            {
              label: t("Role"),
              value: employmentInfo.roleInfo?.role,
            },
            {
              label: t("Department"),
              value: employmentInfo.departmentInfo?.name,
            },
            {
              label: t("Designation"),
              value: employmentInfo.designationInfo?.name,
            },
            {
              label: t("Job Title"),
              value: employmentInfo.jobTitleInfo?.name,
            },
            { label: t("Job Grade"), value: employmentInfo.jobGrade },
            { label: t("Contract Type"), value: employmentInfo.contractType },
            {
              label: t("Employment Type"),
              value: employmentInfo.employeeType,
            },
            {
              label: t("Joining Date"),
              value:
                employmentInfo.joiningDate || employmentInfo.joinDate
                  ? formatDateToDotFormat(
                      employmentInfo.joiningDate || employmentInfo.joinDate
                    )
                  : "N/A",
            },
            {
              label: t("Reporting To"),
              value: employmentInfo.reportingToInfo
                ? `${employmentInfo.reportingToInfo.firstName || ""} ${
                    employmentInfo.reportingToInfo.lastName || ""
                  }${
                    employmentInfo.reportingToInfo.email
                      ? ` (${employmentInfo.reportingToInfo.email})`
                      : ""
                  }`.trim() || "-"
                : "-",
            },
            {
              label: t("Probation Period"),
              value: employmentInfo.probationPeriod
                ? `${employmentInfo.probationPeriod} ${t("days")}`
                : "N/A",
            },
          ]
        : [],
    },
    ...(!isCitizen
      ? [
          {
            key: "dependents",
            title: t("Dependents Information"),
            icon: <Users className="w-6 h-6 md:w-8 md:h-8 text-[#1A6D73]" />,
            type: "dependents",
            data: dependentsInfo || [],
          },
        ]
      : []),
  ];

  const DependentsSection = ({ title, icon, dependents, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];

    const formatDependentData = (dependent) =>
      [
        { label: t("First Name"), value: dependent.dependentFirstName },
        { label: t("Second Name"), value: dependent.dependentSecondName },
        { label: t("Third Name"), value: dependent.dependentThirdName },
        { label: t("Fourth Name"), value: dependent.dependentFourthName },
        { label: t("Iqama Number"), value: dependent.iqamaNo },
        {
          label: t("Iqama Expiry Date"),
          value: dependent.iqamaExpiryDate
            ? formatDateToDotFormat(dependent.iqamaExpiryDate)
            : "N/A",
        },
        { label: t("Passport Number"), value: dependent.passportNo },
        {
          label: t("Passport Expiry Date"),
          value: dependent.passportExpiryDate
            ? formatDateToDotFormat(dependent.passportExpiryDate)
            : "N/A",
        },
        { label: t("Contact Number"), value: dependent.contactNo },
        {
          label: t("Date of Birth"),
          value: dependent.dateOfBirth
            ? formatDateToDotFormat(dependent.dateOfBirth)
            : "N/A",
        },
        {
          label: t("Age"),
          value: dependent.dateOfBirth
            ? `${calculateAge(dependent.dateOfBirth)} years`
            : "N/A",
        },
        { label: t("Relation"), value: dependent.relation },
        { label: t("Religion"), value: dependent.religionInfo?.name },
        { label: t("Nationality"), value: dependent.nationality?.name },
        { label: t("Sponsor Name"), value: dependent.sponsorName },
        { label: t("Sponsor ID"), value: dependent.sponsorId },
        {
          label: t("Citizenship Status"),
          value: dependent.citizen === "true" ? t("Citizen") : t("Non-Citizen"),
        },
        { label: t("Marital Status"), value: dependent.maritalStatus },
        { label: t("Gender"), value: dependent.gender },
      ].filter(
        (item) => item.value && item.value !== "N/A" && item.value.trim() !== ""
      );

    return (
      <div className="mb-6">
        <div className="bg-white rounded-lg">
          <div
            className="p-4 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(sectionKey)}
          >
            <div className="flex items-center gap-2">
              {icon}
              <h2 className="text-[#04080B] font-medium text-xl whitespace-nowrap">
                {title}
              </h2>
              {dependents.length > 0 && (
                <span className="bg-[#1A6D73] text-white text-xs px-2 py-1 rounded-full ml-2">
                  {dependents.length}
                </span>
              )}
            </div>
            <button className="text-[#475467]">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {isExpanded && (
            <div className="px-4 pb-4">
              {!dependents || dependents.length === 0 ? (
                // Empty State
                <div className="flex flex-col items-center justify-center py-8 px-4">
                  <div className="w-12 h-12 bg-[#F9FAFB] rounded-full flex items-center justify-center mb-3">
                    <Users className="w-6 h-6 text-[#98A2B3]" />
                  </div>
                  <h3 className="text-[#475467] font-medium mb-1">
                    {t("No dependents information available")}
                  </h3>
                  <p className="text-[#98A2B3] text-sm text-center">
                    {t("There are no dependents registered for this employee.")}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {dependents.map((dependent, index) => {
                    const dependentData = formatDependentData(dependent);
                    return (
                      <div
                        key={index}
                        className="border border-[#EAECF0] rounded-lg p-4"
                      >
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-[#1A6D73] rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {index + 1}
                          </div>
                          <h3 className="font-medium text-[#04080B]">
                            {`${dependent.dependentFirstName || ""} ${
                              dependent.dependentSecondName || ""
                            } ${dependent.dependentThirdName || ""} ${
                              dependent.dependentFourthName || ""
                            }`.trim() ||
                              t("Dependent {{number}}", { number: index + 1 })}
                          </h3>
                          <span className="text-sm text-[#475467] bg-[#F9FAFB] px-2 py-1 rounded">
                            {dependent.relation || t("Dependent")}
                          </span>
                        </div>

                        {/* Desktop Grid View */}
                        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {dependentData.map((item, idx) => (
                            <div
                              key={idx}
                              className="bg-[#F9FAFB] p-3 rounded-lg"
                            >
                              <div className="text-[#475467] text-sm font-medium mb-1">
                                {item.label}
                              </div>
                              <div className="text-[#04080B] font-medium">
                                {item.value || "N/A"}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Mobile Stack View */}
                        <div className="md:hidden space-y-3">
                          {dependentData.map((item, idx) => (
                            <div
                              key={idx}
                              className="bg-[#F9FAFB] p-3 rounded-lg"
                            >
                              <div className="text-[#475467] text-sm font-medium mb-1">
                                {item.label}
                              </div>
                              <div className="text-[#04080B] font-medium">
                                {item.value || "N/A"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Data-driven configuration for document sections
  const getDocumentSections = () => [
    {
      key: "medical",
      title: t("Medical Information"),
      icon: <HeartIcon className="w-6 h-6 md:w-8 md:h-8 text-[#1A6D73]" />,
      type: "documents",
      data: medicalDocuments,
    },
    {
      key: "others",
      title: t("Other Information"),
      icon: <BriefcaseIcon className="w-6 h-6 md:w-8 md:h-8 text-[#1A6D73]" />,
      type: "documents",
      data: [...employmentDocuments, ...immigrationInfo, ...vehicleDocuments],
    },
  ];

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const getEmployeeRegistrationData = async (empPublicId) => {
    setIsLoading(true);
    try {
      const response = await fetchEmployeeRegistrationDataById(empPublicId);
      if (response?.data) {
        const completeData = response?.data?.data;
        setEmployeeRegistrationData(completeData);
        setPersonalInfo(completeData?.personalInfo);
        setIsCitizen(completeData?.personalInfo?.citizen == "true");
        setEmploymentInfo(completeData?.employementInfo);
        setNationalIdDetails(completeData?.nationalIdInfo);
        setSalaryDetails(completeData?.salaryInfo);
        setIqamaDetails(completeData?.iqamaInfo);
        setAccountDetails(completeData?.bankAccountInfo);
        setHealthInsuranceDetails(completeData?.healthInfo);
        setHealthCardDetails(completeData?.healthCardInfo);
        setPassportDetails(completeData?.passportInfo);
        setVisaDetails(completeData?.visaInfo);
        setContractDetails(completeData?.contractInfo);
        setDrivingLicenseDetails(completeData?.drivingInfo);
        setSubscriptionDetails(completeData?.subscriptionInfo);
        setDependentsInfo(completeData?.dependentInfo || null);
        setIsLoading(false);
      }
    } catch (res) {
      notifyWarn(res?.message);
      console.log(res);
      setIsLoading(false);
    }
  };

  console.log(
    "seprate states: ",
    employeeRegistrationData,
    visaDetails,
    iqamaDetails,
    salaryDetails,
    healthCardDetails,
    healthInsuranceDetails,
    drivingLicenseDetails
  );

  console.log("show tab modal: ", showProfileModal);

  const handleViewFullDetails = () => {
    setShowProfileModal(true);
  };

  const handleViewDocument = (document) => {
    console.log("documents: ", document);
    if (!document) {
      notifyWarn(t("Document not available"));
      return;
    }
    setSelectedDocument(document);
    setDocumentViewerOpen(true);
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const calculateRemainingLife = (expiryDate) => {
    if (!expiryDate) return "N/A";
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    if (diffTime < 0) return "Expired";
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffMonths / 12);

    if (diffYears > 0) {
      return `${diffYears} years, ${diffMonths % 12} months`;
    } else if (diffMonths > 0) {
      return `${diffMonths} months, ${diffDays % 30} days`;
    } else {
      return `${diffDays} days`;
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Generic Info Section Component
  const InfoSection = ({ title, icon, data, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];

    return (
      <div className="mb-6">
        <div className="bg-white rounded-lg">
          <div
            className="p-4 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(sectionKey)}
          >
            <div className="flex items-center gap-2">
              {icon}
              <h2 className="text-[#04080B] font-medium text-xl whitespace-nowrap">
                {title}
              </h2>
            </div>
            <button className="text-[#475467]">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {isExpanded && (
            <div className="px-4 pb-4">
              {!data || data.length === 0 ? (
                // Empty State
                <div className="flex flex-col items-center justify-center py-8 px-4">
                  <div className="w-12 h-12 bg-[#F9FAFB] rounded-full flex items-center justify-center mb-3">
                    <div className="w-6 h-6 text-[#98A2B3]">{icon}</div>
                  </div>
                  <h3 className="text-[#475467] font-medium mb-1">
                    {t("No {{title}} available", {
                      title: title.toLowerCase(),
                    })}
                  </h3>
                  <p className="text-[#98A2B3] text-sm text-center">
                    {t(
                      "There's no information to display in this section yet."
                    )}
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Grid View */}
                  <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {data.map((item, idx) => (
                      <div key={idx} className="bg-[#F9FAFB] p-3 rounded-lg">
                        <div className="text-[#475467] text-sm font-medium mb-1">
                          {item.label}
                        </div>
                        <div className="text-[#04080B] font-medium">
                          {item.value || "N/A"}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile Stack View */}
                  <div className="md:hidden space-y-3">
                    {data.map((item, idx) => (
                      <div key={idx} className="bg-[#F9FAFB] p-3 rounded-lg">
                        <div className="text-[#475467] text-sm font-medium mb-1">
                          {item.label}
                        </div>
                        <div className="text-[#04080B] font-medium">
                          {item.value || "N/A"}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Component for mobile view of documents
  const MobileDocumentCard = ({ doc }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{doc.type}</h3>
        </div>
        <span
          className={`flex items-center px-2 py-1 rounded-full text-xs ${
            doc.status === "No Data"
              ? "bg-[#F2F4F7] text-[#475467]"
              : "bg-[#ECFDF3] text-[#027A48]"
          }`}
        >
          <Dot size={16} />
          {doc.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-[#475467]">{t("Number/ID")}:</p>
          <p className="font-medium">{doc.number}</p>
        </div>
        <div>
          <p className="text-[#475467]">{t("Issue Date")}:</p>
          <p className="font-medium">{doc.issueDate}</p>
        </div>
        <div>
          <p className="text-[#475467]">{t("Expiry Date")}:</p>
          <p className="font-medium">{doc.expiryDate}</p>
        </div>
        <div>
          <p className="text-[#475467]">{t("Remaining Life")}:</p>
          <p className="font-medium">{doc.remainingLife}</p>
        </div>
      </div>
      <div className="mt-3">
        <button
          onClick={() =>
            handleViewDocument({
              url: `${doc.documents}`,
              label: `${doc.label}`,
              type: "image",
            })
          }
          className={`${
            doc.documents
              ? "text-[#B99A51] cursor-pointer"
              : "text-gray-400 cursor-not-allowed"
          } text-sm font-medium`}
        >
          {doc.documents ? t("View Document") : t("Document Not Available")}
        </button>
      </div>
    </div>
  );

  console.log(
    "documents: ",
    medicalDocuments,
    vehicleDocuments,
    employmentDocuments
  );

  const DocumentSection = ({ title, icon, documents, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];

    return (
      <div className="mb-6">
        <div className="bg-white rounded-lg">
          <div
            className="p-4 flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(sectionKey)}
          >
            <div className="flex items-center gap-2">
              {icon}
              <h2 className="text-[#04080B] font-medium text-xl whitespace-nowrap">
                {title}
              </h2>
            </div>
            <button className="text-[#475467]">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {isExpanded && (
            <>
              {/* Desktop view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full lg:table-fixed">
                  <thead>
                    <tr className="bg-[#F2F4F7] text-[#475467] text-sm">
                      <th className="flex items-center gap-2 p-3 text-left font-bold">
                        {t("Document Type")}
                      </th>
                      <th className="p-3 text-left font-bold border-r-2 border-[#F2F4F7]">
                        {t("Number/ID")}
                      </th>
                      <th className="p-3 text-left font-bold">
                        {t("Issue Date")}
                      </th>
                      <th className="p-3 text-left font-bold">
                        {t("Expiry Date")}
                      </th>
                      <th className="p-3 text-left font-bold">
                        {t("Remaining Life")}
                      </th>
                      <th className="p-3 text-left font-bold">{t("Status")}</th>
                      <th className="p-3 text-left font-bold">
                        {t("Document")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc, idx) => (
                      <tr key={idx} className="border-b border-[#EAECF0]">
                        <td className="p-3 flex items-center gap-2">
                          {doc.type}
                        </td>
                        <td className="p-3 text-left">{doc.number}</td>
                        <td className="p-3 text-left">{doc.issueDate}</td>
                        <td className="p-3 text-left">{doc.expiryDate}</td>
                        <td className="p-3 text-left">{doc.remainingLife === "Expired" ? t("Expired") : doc.remainingLife}</td>
                        <td className="p-3 text-left">
                          <span
                            className={`flex w-fit md:items-center text-left px-2 py-1 rounded-full text-sm ${
                              doc.status === "No Data"
                                ? "bg-[#F2F4F7] text-[#475467]"
                                : "bg-[#ECFDF3] text-[#027A48]"
                            }`}
                          >
                            <Dot size={16} />
                            {t(doc.status)}
                          </span>
                        </td>
                        <td className="p-3 text-left">
                          <button
                            onClick={() =>
                              handleViewDocument({
                                url: `${doc.documents}`,
                                label: `${doc.label}`,
                                type: "image",
                              })
                            }
                            className={`${
                              doc.documents
                                ? "text-[#B99A51] cursor-pointer"
                                : "text-gray-400 cursor-not-allowed"
                            } text-sm font-medium`}
                          >
                            {doc.documents ? t("Document") : t("Not Available")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile view */}
              <div className="md:hidden px-4 pb-4">
                {documents.map((doc, idx) => (
                  <MobileDocumentCard key={idx} doc={doc} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Generic Section Renderer
  const renderSection = (section) => {
    if (section.type === "info") {
      return (
        <InfoSection
          key={section.key}
          title={section.title}
          icon={section.icon}
          data={section.data}
          sectionKey={section.key}
        />
      );
    } else if (section.type === "documents") {
      return (
        <DocumentSection
          key={section.key}
          title={section.title}
          icon={section.icon}
          documents={section.data}
          sectionKey={section.key}
        />
      );
    } else if (section.type === "dependents") {
      return (
        <DependentsSection
          key={section.key}
          title={section.title}
          icon={section.icon}
          dependents={section.data}
          sectionKey={section.key}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-[9999]">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center">
            {isLoading ? (
              <PageLoader />
            ) : (
              <DialogPanel className="w-full max-w-[1778px] max-h-[90vh] bg-[#F2F4F7] rounded-xl overflow-y-auto scrollbar-hide my-auto">
                {/* Header */}
                <div className="bg-[#1A6D73] p-4 sm:p-6 flex flex-col md:flex-row items-center md:items-start gap-4 relative">
                  {/* Close button for mobile - positioned absolutely */}
                  <div
                    onClick={onClose}
                    className="absolute top-2 right-2 p-1 bg-[#879697] rounded-full hover:bg-[#749193] transition-colors duration-200 md:hidden"
                  >
                    <img
                      src={cancelIcon}
                      alt="cancel icon"
                      className="w-5 h-5 cursor-pointer"
                    />
                  </div>

                  <div className="w-16 h-16 rounded-full overflow-hidden bg-[#1A6D73] flex items-center justify-center mt-4 md:mt-0">
                    <img
                      src={
                        personalInfo?.profilePicture?.fileUrl ||
                        role_employeeIcon1
                      }
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
                      <span className="text-[#CDF1F4] text-sm">
                        {employmentInfo?.employeeId}
                      </span>
                      <span className="text-white font-semibold">
                        {`${personalInfo?.firstName} ${
                          personalInfo?.secondName || ""
                        } ${personalInfo?.thirdName || ""} ${
                          personalInfo?.lastName
                        }`}
                      </span>
                      <div className="hidden md:block h-6 w-[2px] bg-gray-300 mx-2"></div>
                      <div className="bg-[#0E474B] gap-2 text-white px-4 py-1 rounded-full text-sm flex items-center">
                        {isCitizen && (
                          <div className="w-5 h-5 rounded-full overflow-hidden bg-[#1A6D73] flex items-center justify-center">
                            <img
                              src={Flag}
                              alt="flag"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <span className="text-[#CDF1F4] text-sm">
                          {isCitizen
                            ? t("Saudi")
                            : personalInfo?.nationalityInfo?.name || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <span className="bg-[#0E474B] text-white px-3 py-1 rounded-full text-xs sm:text-sm">
                        {personalInfo?.gender || "N/A"}
                      </span>

                      <span className="bg-[#0E474B] text-white px-3 py-1 rounded-full text-xs sm:text-sm">
                        {employmentInfo?.departmentInfo?.name || "N/A"}
                      </span>

                      <span className="bg-[#0E474B] text-white px-3 py-1 rounded-full text-xs sm:text-sm">
                        {employmentInfo?.jobTitleInfo?.name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="w-auto md:w-[188px] text-center md:pt-4 font-bold text-lg md:text-xl text-white relative md:block flex flex-row items-center justify-center mt-4 md:mt-0">
                    {/* Cancel icon container - desktop only */}
                    <div
                      onClick={onClose}
                      className="hidden md:block absolute top-[-20px] right-[-10px] p-0 bg-[#879697] rounded-full hover:bg-[#749193] transition-colors duration-200"
                    >
                      <img
                        src={cancelIcon}
                        alt="cancel icon"
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>

                    {/* View Full Details button */}
                    <button
                      className="underline hover:text-[#CDF1F4] transition-colors duration-200"
                      onClick={handleViewFullDetails}
                    >
                      {t("View Full Details")}
                    </button>
                  </div>
                </div>

                {/* Content - Updated to show 4 main categories */}
                <div className="p-3 sm:p-6">
                  {/* 1. Personal Information */}
                  {getInfoSections()
                    .filter((section) => section.key === "personal")
                    .map((section) => renderSection(section))}

                  {/* 2. Employment Information */}
                  {getInfoSections()
                    .filter((section) => section.key === "employment")
                    .map((section) => renderSection(section))}

                  {/* 4. Medical Documents */}
                  {getDocumentSections()
                    .filter((section) => section.key === "medical")
                    .map((section) => renderSection(section))}

                  {/* 3. Dependents Information - ADD THIS SECTION */}
                  {getInfoSections()
                    .filter((section) => section.key === "dependents")
                    .map((section) => renderSection(section))}

                  {/* 5. Other Documents */}
                  {getDocumentSections()
                    .filter((section) => section.key === "others")
                    .map((section) => renderSection(section))}
                </div>
              </DialogPanel>
            )}
            {showProfileModal && (
              <EmployeeDetails
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                onBack={() => setShowProfileModal(false)}
                personalInfo={personalInfo}
                employementInfo={employmentInfo}
                nationalIdDetails={nationalIdDetails}
                salaryDetails={salaryDetails}
                iqamaDetails={iqamaDetails}
                accountDetails={accountDetails}
                healthInsuranceDetails={healthInsuranceDetails}
                healthCardDetails={healthCardDetails}
                passportDetails={passportDetails}
                visaDetails={visaDetails}
                contractDetails={contractDetails}
                drivingLicenseDetails={drivingLicenseDetails}
                subscriptionDetails={subscriptionDetails}
                dependentsInfo={dependentsInfo}
                employeeGeneralInfo={employeeGeneralInfo}
                empPublicId={empPublicId}
                getEmployeeRegistrationData={getEmployeeRegistrationData}
                handleGetTableData={handleGetTableData}
                currentEmployeeEditing={currentEmployeeEditing}
              />
            )}
          </div>
        </div>
      </Dialog>
      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
      />
    </>
  );
};

export default EmployeeModal;
